import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr("");
    try {
      await login(email.trim(), password);
      const to = loc.state?.from || "/dashboard";
      toast.success("Welcome back");
      nav(to, { replace: true });
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : "Login failed";
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] grid place-items-center px-6">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 mb-8" data-testid="login-brand-link">
          <span className="h-7 w-7 grid place-items-center bg-zinc-950 text-white rounded-sm">
            <Sparkles className="h-4 w-4" />
          </span>
          <span className="font-display text-[17px] font-semibold tracking-tight text-zinc-950">
            PortalStudio
          </span>
        </Link>

        <div className="border border-zinc-200 bg-white rounded-md p-8">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-3">Sign in</div>
          <h1 className="font-display text-2xl font-semibold text-zinc-950 tracking-tight">Welcome back</h1>
          <p className="text-sm text-zinc-500 mt-1">Access your school's components.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <Label className="text-xs text-zinc-500">Email</Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5"
                data-testid="login-email-input"
                autoComplete="email"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Password</Label>
              <Input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5"
                data-testid="login-password-input"
                autoComplete="current-password"
              />
            </div>

            {err ? (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2" data-testid="login-error">
                {err}
              </div>
            ) : null}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="w-full h-11 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 transition-all duration-200"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Sign in
            </Button>
          </form>
        </div>

        <p className="text-xs text-zinc-500 mt-6 text-center">
          Need an account? Ask your system administrator.
        </p>
      </div>
    </div>
  );
}
